import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, interval, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ConvsService } from './convs.service';
import { DomainService } from './domain.service';
import { JobsService } from './jobs.service';

@Injectable({
  providedIn: 'root'
})
export class SpeedTestService {
  private totalMessages = new BehaviorSubject<number>(0);
  private elapsedMs = new BehaviorSubject<number>(0);
  private timerSub: Subscription | null = null;

  totalMessages$ = this.totalMessages.asObservable();
  elapsedMs$ = this.elapsedMs.asObservable();

  /** Derived: messages per second (0 when no time has elapsed) */
  messagesPerSecond$ = combineLatest([this.totalMessages$, this.elapsedMs$]).pipe(
    map(([msgs, ms]) => (ms > 0 ? msgs / (ms / 1000) : 0))
  );

  /** Whether the speed test overlay should be visible */
  isSpeedTestEnabled$ = combineLatest([
    this.jobsService.currentJob.asObservable(),
    this.domainService.domain.asObservable()
  ]).pipe(
    map(([job, domain]) => !!(job?.is_speed_test || domain?.is_speed_test)),
    distinctUntilChanged()
  );

  constructor(
    private convsService: ConvsService,
    private domainService: DomainService,
    private jobsService: JobsService
  ) {
    this.subscribeToFastMode();
  }

  /** Called by StatsService when a text/MMS event is performed */
  recordMessage(): void {
    if (this.convsService.isFastModeEnabled.value) {
      this.totalMessages.next(this.totalMessages.value + 1);
    }
  }

  /** Reset all stats to zero */
  resetStats(): void {
    this.totalMessages.next(0);
    this.elapsedMs.next(0);
  }

  /** Start/stop the elapsed-time timer based on fast mode state */
  private subscribeToFastMode(): void {
    this.convsService.isFastModeEnabled$.subscribe(enabled => {
      if (enabled) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    });
  }

  private startTimer(): void {
    if (this.timerSub) {
      return;
    }
    this.timerSub = interval(100).subscribe(() => {
      this.elapsedMs.next(this.elapsedMs.value + 100);
    });
  }

  private stopTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = null;
    }
  }
}
